
import { useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  UtensilsCrossed, 
  Wine, 
  Music, 
  Calendar, 
  Hotel, 
  Sparkles, 
  Scissors,
  ArrowRight,
  QrCode,
  CreditCard,
  BarChart3,
  Users
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

const businessTypes = [
  {
    id: "restaurants",
    name: "Restaurants",
    icon: UtensilsCrossed,
    color: "from-orange-500 to-red-500",
    image: "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80",
    benefits: [
      { icon: QrCode, title: "QR Menu System", description: "Contactless digital menus that update instantly" },
      { icon: CreditCard, title: "Table-side Payments", description: "Secure payments without waiting for staff" },
      { icon: BarChart3, title: "Order Analytics", description: "Track popular dishes and optimize your menu" },
      { icon: Users, title: "Staff Management", description: "Streamline operations with integrated staff tools" }
    ]
  },
  {
    id: "bars",
    name: "Bars",
    icon: Wine,
    color: "from-purple-500 to-pink-500",
    image: "https://images.unsplash.com/photo-1566737236500-c8ac43014a8e?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80",
    benefits: [
      { icon: QrCode, title: "Digital Drink Menus", description: "Showcase cocktails with beautiful imagery" },
      { icon: CreditCard, title: "Fast Bar Payments", description: "Reduce wait times during busy hours" },
      { icon: BarChart3, title: "Sales Insights", description: "Track bestselling drinks and peak hours" },
      { icon: Users, title: "Bartender Tools", description: "Manage orders and inventory efficiently" }
    ]
  },
  {
    id: "nightclubs",
    name: "Night Clubs",
    icon: Music,
    color: "from-violet-500 to-purple-500",
    image: "https://images.unsplash.com/photo-1605810230434-7631ac76ec81?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80",
    benefits: [
      { icon: QrCode, title: "VIP Table Service", description: "Premium ordering experience for VIP guests" },
      { icon: CreditCard, title: "Cashless Transactions", description: "Safe and secure payments in crowded venues" },
      { icon: BarChart3, title: "Event Analytics", description: "Track performance across different events" },
      { icon: Users, title: "Staff Coordination", description: "Coordinate large teams during busy nights" }
    ]
  },
  {
    id: "events",
    name: "Events",
    icon: Calendar,
    color: "from-blue-500 to-cyan-500",
    image: "https://images.unsplash.com/photo-1511795409834-ef04bbd61622?ixlib=rb-4.0.3&auto=format&fit=crop&w=2069&q=80",
    benefits: [
      { icon: QrCode, title: "Event Catering", description: "Manage food and beverage orders at large events" },
      { icon: CreditCard, title: "Group Payments", description: "Handle multiple payment methods seamlessly" },
      { icon: BarChart3, title: "Event Reporting", description: "Comprehensive analytics for event success" },
      { icon: Users, title: "Team Management", description: "Coordinate staff across multiple venues" }
    ]
  },
  {
    id: "hotels",
    name: "Hotels",
    icon: Hotel,
    color: "from-emerald-500 to-teal-500",
    image: "https://images.unsplash.com/photo-1721322800607-8c38375eef04?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80",
    benefits: [
      { icon: QrCode, title: "Room Service", description: "In-room dining with digital ordering" },
      { icon: CreditCard, title: "Guest Billing", description: "Seamless integration with hotel billing systems" },
      { icon: BarChart3, title: "Guest Analytics", description: "Understand guest preferences and habits" },
      { icon: Users, title: "Concierge Tools", description: "Empower staff to provide exceptional service" }
    ]
  },
  {
    id: "spas",
    name: "Spas",
    icon: Sparkles,
    color: "from-pink-500 to-rose-500",
    image: "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80",
    benefits: [
      { icon: QrCode, title: "Service Booking", description: "Digital booking and treatment menus" },
      { icon: CreditCard, title: "Spa Payments", description: "Quiet, contactless payment processing" },
      { icon: BarChart3, title: "Treatment Analytics", description: "Track popular services and therapist performance" },
      { icon: Users, title: "Therapist Scheduling", description: "Optimize staff schedules and availability" }
    ]
  },
  {
    id: "salons",
    name: "Salons",
    icon: Scissors,
    color: "from-amber-500 to-orange-500",
    image: "https://images.unsplash.com/photo-1560066984-138dadb4c035?ixlib=rb-4.0.3&auto=format&fit=crop&w=2074&q=80",
    benefits: [
      { icon: QrCode, title: "Service Menus", description: "Showcase services with pricing and descriptions" },
      { icon: CreditCard, title: "Appointment Payments", description: "Process payments before or after services" },
      { icon: BarChart3, title: "Stylist Performance", description: "Track bookings and customer satisfaction" },
      { icon: Users, title: "Staff Scheduling", description: "Manage appointments and staff availability" }
    ]
  }
];

export function BusinessTypesSection() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("restaurants");

  const activeBusiness = businessTypes.find(b => b.id === activeTab);

  return (
    <section className="py-16 bg-gradient-to-br from-muted/50 to-background">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Built for Every Business
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            From intimate cafes to bustling nightclubs, Xerven adapts to your unique business needs
          </p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid grid-cols-3 md:grid-cols-7 lg:grid-cols-7 w-full mb-8 h-auto p-1">
            {businessTypes.map((business) => {
              const IconComponent = business.icon;
              return (
                <TabsTrigger
                  key={business.id}
                  value={business.id}
                  className="flex flex-col items-center gap-2 py-4 px-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
                >
                  <IconComponent className="h-5 w-5" />
                  <span className="text-xs font-medium">{business.name}</span>
                </TabsTrigger>
              );
            })}
          </TabsList>

          {businessTypes.map((business) => {
            const IconComponent = business.icon;
            return (
              <TabsContent key={business.id} value={business.id} className="mt-8">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                  {/* Left side - Image and overview */}
                  <div className="order-2 lg:order-1">
                    <div className="relative overflow-hidden rounded-2xl shadow-2xl">
                      <img
                        src={business.image}
                        alt={`${business.name} using Xerven`}
                        className="w-full h-[400px] object-cover"
                      />
                      <div className={`absolute inset-0 bg-gradient-to-tr ${business.color} opacity-20`} />
                      <div className="absolute bottom-6 left-6 right-6">
                        <div className="bg-background/90 backdrop-blur-sm rounded-lg p-4">
                          <div className="flex items-center gap-3 mb-2">
                            <div className={`p-2 rounded-lg bg-gradient-to-r ${business.color}`}>
                              <IconComponent className="h-5 w-5 text-white" />
                            </div>
                            <h3 className="text-lg font-semibold">{business.name}</h3>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            Streamline operations and enhance customer experience
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right side - Benefits */}
                  <div className="order-1 lg:order-2 space-y-6">
                    <div>
                      <h3 className="text-2xl font-bold mb-4">
                        Perfect for {business.name}
                      </h3>
                      <p className="text-muted-foreground mb-6">
                        Discover how Xerven transforms your {business.name.toLowerCase()} operations with cutting-edge technology
                      </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {business.benefits.map((benefit, index) => {
                        const BenefitIcon = benefit.icon;
                        return (
                          <Card key={index} className="border-0 shadow-md hover:shadow-lg transition-shadow duration-300">
                            <CardContent className="p-4">
                              <div className="flex items-start gap-3">
                                <div className={`p-2 rounded-lg bg-gradient-to-r ${business.color} flex-shrink-0`}>
                                  <BenefitIcon className="h-4 w-4 text-white" />
                                </div>
                                <div>
                                  <h4 className="font-semibold text-sm mb-1">{benefit.title}</h4>
                                  <p className="text-xs text-muted-foreground">{benefit.description}</p>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>

                    <div className="pt-4">
                      <Button asChild size="lg" className={`bg-gradient-to-r ${business.color} hover:opacity-90 text-white border-0`}>
                        <Link to="/register">
                          Get Started with {business.name}
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Link>
                      </Button>
                    </div>
                  </div>
                </div>
              </TabsContent>
            );
          })}
        </Tabs>
      </div>
    </section>
  );
}
